/* tslint:disable: jsx-no-lambda */
import * as React from 'react';
import {
  Button,
  TextField,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import matchSorter from 'match-sorter';
import Avatar from '@material-ui/core/Avatar';
import MuiAlert from '@material-ui/lab/Alert';
import DataTable from 'react-data-table-component';
import { formatDate } from 'utils/utils';
import { TEventTarget } from 'types';
import { listAllExcel, downloadExcel } from 'services/AdminUsersControlService';
import ExcelLogo from '../../../assets/Avatar/excel.png';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    avatar: {
      marginLeft: 100,
    },
  })
);

interface IReportConfig {
  id: string;
  title: string;
  tipo: string;
  description: string;
  bucketName: string;
}

interface IExcel {
  Key: string;
  LastModified: string;
  bucketName?: string;
}

const REPORT_TYPES: IReportConfig[] = [
  {
    id: 'monthly',
    title: 'Reporte Cotizaciones Mensuales',
    tipo: 'mensual',
    description: 'Reporte mensual de cotizaciones',
    bucketName: 'cavca-api-dev-s3',
  },
  {
    id: 'weekly',
    title: 'Reporte Semanal (Lunes)',
    tipo: 'cotizaciones',
    description: 'Reporte semanal generado los lunes',
    bucketName: 'cavca-api-dev-s3-reporte-v2',
  },
];

const DownloadExcel = () => {
  const classes = useStyles();

  const [openSnack, setOpenSnack] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [filteredOpportunities, setFilteredOpportunities] = React.useState<string>('');
  const [userCotizaciones, setUserCotizaciones] = React.useState<IExcel[] | null>(null);
  const [selectedReport, setSelectedReport] = React.useState<string>('monthly');

  const filteredItems =
    userCotizaciones !== null
      ? matchSorter(userCotizaciones, filteredOpportunities, {
          keys: ['Key', 'LastModified'],
        })
      : [];

  const onFilterSearch = (e: TEventTarget) => setFilteredOpportunities(e.target.value);

  const onError = (error: string) => {
    setOpenSnack(true);
    if (error.includes('NoSuchKey')) {
      setErrorMessage(
        'El archivo no existe o aún no ha sido generado. Por favor, intente más tarde.'
      );
    } else {
      setErrorMessage(error);
    }
    setLoading(false);
  };

  const onCloseSnackBar = () => {
    setOpenSnack(false);
  };

  const downloadExcels = (Key: string) => {
    setLoading(true);
    const selectedConfig = REPORT_TYPES.find((report) => report.id === selectedReport);

    downloadExcel(
      Key,
      selectedConfig?.tipo || 'mensual',
      selectedConfig?.bucketName || 'cavca-api-dev-s3',
      onError
    )
      .then((response) => {
        if (response && response.urlDowload) {
          window.open(response.urlDowload);
        } else {
          onError('No se pudo generar el enlace de descarga');
        }
        setLoading(false);
      })
      .catch((error) => {
        onError(error.message || 'Error al descargar el archivo');
        setLoading(false);
      });
  };

  const handleReportChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newReportType = event.target.value as string;
    setSelectedReport(newReportType);
    setLoading(true);
    const selectedConfig = REPORT_TYPES.find((report) => report.id === newReportType);

    listAllExcel(
      selectedConfig?.tipo || 'mensual',
      selectedConfig?.bucketName || 'cavca-api-dev-s3',
      onError
    )
      .then((response) => {
        if (response && Array.isArray(response)) {
          setUserCotizaciones(response);
        } else {
          setUserCotizaciones([]);
          onError('No se encontraron reportes disponibles');
        }
        setLoading(false);
      })
      .catch((error) => {
        onError(error.message || 'Error al cargar los reportes');
        setLoading(false);
      });
  };

  const columnsCotizaciones = [
    {
      name: '',
      selector: (row: IExcel) => <Avatar className={classes.avatar} src={ExcelLogo} />,
      sortable: true,
    },
    {
      name: 'Nombre del Archivo',
      selector: (row: IExcel) => row.Key,
      sortable: true,
    },
    {
      name: 'Ultima Actualizacion',
      selector: (row: IExcel) => formatDate(row.LastModified),
      sortable: true,
    },
    {
      name: '',
      selector: (row: IExcel) => (
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={() => downloadExcels(row.Key)}>
          DESCARGAR
        </Button>
      ),
    },
  ];

  React.useEffect(() => {
    setLoading(true);
    const selectedConfig = REPORT_TYPES.find((report) => report.id === selectedReport);

    listAllExcel(
      selectedConfig?.tipo || 'mensual',
      selectedConfig?.bucketName || 'cavca-api-dev-s3',
      onError
    ).then((response) => {
      setUserCotizaciones(response);
      setLoading(false);
    });
  }, [selectedReport]);

  return (
    <React.Fragment>
      <Grid container spacing={3} style={{ marginTop: '20px', padding: '20px' }}>
        <Grid item xs={12}>
          <div style={{ marginBottom: '30px', paddingTop: '10px' }}>
            <FormControl variant="outlined" style={{ minWidth: 250 }}>
              <InputLabel>Tipo de Reporte</InputLabel>
              <Select value={selectedReport} onChange={handleReportChange} label="Tipo de Reporte">
                {REPORT_TYPES.map((report) => (
                  <MenuItem key={report.id} value={report.id}>
                    {report.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <DataTable
            title={REPORT_TYPES.find((report) => report.id === selectedReport)?.title}
            columns={columnsCotizaciones}
            progressPending={loading}
            paginationComponentOptions={{
              rowsPerPageText: 'Filas por página:',
              rangeSeparatorText: 'de',
            }}
            noDataComponent="No se han encontrado reportes"
            data={filteredItems}
            defaultSortField="year"
            pagination={true}
            highlightOnHover
            subHeader={true}
            subHeaderComponent={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  id="outlined-basic"
                  label="Buscar"
                  variant="outlined"
                  size="small"
                  style={{ margin: '5px' }}
                  onChange={onFilterSearch}
                />
              </div>
            }
            fixedHeaderScrollHeight="700px"
          />
        </Grid>
      </Grid>
      <Snackbar open={openSnack} autoHideDuration={6000} onClose={onCloseSnackBar}>
        <MuiAlert elevation={6} variant="filled" onClose={onCloseSnackBar} severity="error">
          {errorMessage}
        </MuiAlert>
      </Snackbar>
    </React.Fragment>
  );
};

export default DownloadExcel;
